import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import SelectShow from "@pages/Home/components/HomeHeader/SelectShow";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import SelectAmong, { SelectAmongWithContext } from "@vms/SelectAmong";
import { PeopleViewFilterSortContext, usePeopleViewContext } from "@pages/Home/components/PeopleView/PeopleViewContext";
import SelectDisplayMetric from "@vms/SelectDisplayMetric";
import { useMemo, useState } from "react";
import { ALL_RESPONDENT_TYPES } from "@models/RespondentType";
import { DEFAULT_SELECT_AMONG_DATA, SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import {
    HomePeopleIndexRequestPayload,
    HomePeopleIndexRequestPayloadMainModel,
    HomePeopleIndexRequestPayloadIndexModel, useHomePeopleIndexData, HomePeopleIndexDataItem,
} from "@api/useHomePeopleIndexData";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { Sort } from "@models";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useMetric } from "@core/CommonDataContext";
import { CopyTableOptions, useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import { useDisplayMetricContext, useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import {
    PeopleViewIndexTableConfigProps,
    usePeopleViewIndexTableConfig
} from "@pages/Home/components/PeopleView/PeopleViewIndexTableConfig";
import { createFilterSortWithContext } from "@hooks/useFilterSortWithContext";
import { HomePeopleRawNormDataItem } from "@api/useHomePeopleRawNormData";

const useFilterSortWithContext = createFilterSortWithContext<HomePeopleRawNormDataItem>(PeopleViewFilterSortContext);

const PeopleViewIndex: FCX = () => {
    const { show, setShow } = usePeopleViewContext();
    const { data: homeSearchData, resetSearch } = useHomeSearchSettingsContext();
    const { displayMetricId, setDisplayMetricId } = useDisplayMetricContext();
    const { search } = useHomeContext();
    const [indexData, setIndexData] = useState<SelectAmongData>(DEFAULT_SELECT_AMONG_DATA);
    const [indexCopyText, setIndexCopyText] = useState<string | null>(null);

    const payload = useMemo(
        (): HomePeopleIndexRequestPayload => ({
            mainModel: {
                respondentTypes: ALL_RESPONDENT_TYPES as HomePeopleIndexRequestPayloadMainModel["respondentTypes"],
                metric: displayMetricId as HomePeopleIndexRequestPayloadMainModel["metric"],
            },
            indexToModel: {
                respondentTypes: indexData.respondentTypes as HomePeopleIndexRequestPayloadIndexModel["respondentTypes"],
            },
        }),
        [displayMetricId, indexData.respondentTypes]
    );

    const { data: rawData, isLoading } = useHomePeopleIndexData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(i => {
            const columns: { [key: string]: number | null } = {};

            i.columns.forEach((i) => {
                columns[`column${i.id}`] = i.sortValue;
            });

            return {
                ...i,
                ...columns,
            };
        }),
        [dataForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSortWithContext(
        mappedDataForSorting,
    );

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): PeopleViewIndexTableConfigProps => ({
            data,
        }),
        [data],
    );

    const tableConfig = usePeopleViewIndexTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage', },
                ...ALL_RESPONDENT_TYPES.map(id => ({
                    id: `column${id}`,
                    name: `column${id}`,
                })),
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomePeopleIndexDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const displayMetric = useMetric(displayMetricId);

    const copyTableOptions = useMemo(
        (): CopyTableOptions => ({
            hideAmong: true,
            additionalRows: [
                [`Index To: ${indexCopyText ?? ""}`],
                [`Metric: ${displayMetric ? displayMetric?.name : 'Base'}`]
            ]
        }),
        [displayMetric, indexCopyText]
    );

    const indexInitialData = useMemo(
        () => {
            return homeSearchData?.indexTo;
        },
        [homeSearchData]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length, copyTableOptions);

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}>
                <SelectShow show={show} setShow={setShow}/>
            </HomeHeader>
            <HomeSubHeader>
                <SelectDisplayMetric
                    value={displayMetricId}
                    setValue={setDisplayMetricId}
                    hidePolarity
                    onChange={resetSearch}
                />
                <SelectAmong
                    label="FOR:"
                    defaultThumbnailText="Please select"
                    showTotalOption
                    useOptionsLimit
                    noBrands
                    allowMultiBrands
                    onApply={resetSearch}
                />
                <SelectAmongWithContext
                    label="INDEX TO:"
                    fieldClassName="appears-left"
                    useGroupsLimit
                    useCoExclusions
                    onDataChange={setIndexData}
                    onCopyTableTextChange={setIndexCopyText}
                    initialData={indexInitialData}
                    onApply={resetSearch}
                />
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={48}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton data={copyTableContent}/>
                        </AsideButtons>
                    </WindowScrollerTable>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
        </>
    );
}

export default PeopleViewIndex;